import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getInventoryById,
  getInventoryStockById,
  getInventoryStockRawById,
  postInventoryAction,
} from '../services/inventoriesService';

// Claves de cache, centralizadas para que el invalidate de la mutación no se
// despeine si se renombra algo. Cualquier query nueva sobre un inventario
// debería usar inventoryKeys.* y no construir tuplas a mano.
export const inventoryKeys = {
  detail: (depositId, inventoryId) => ['inventory', Number(depositId), Number(inventoryId)],
  stock: (depositId, inventoryId, mode, args) => [
    'inventory-stock',
    Number(depositId),
    Number(inventoryId),
    mode,
    args || {},
  ],
};

/**
 * Detalle del inventario. Se invalida automáticamente al ejecutar una acción
 * de la máquina de estados (ver useInventoryActionMutation).
 */
export const useInventoryQuery = (depositId, inventoryId) =>
  useQuery(
    inventoryKeys.detail(depositId, inventoryId),
    () => getInventoryById(depositId, inventoryId).then((r) => r.data),
    {
      enabled: Boolean(depositId && inventoryId),
      // Mantener fresh durante la edición, pero recargar al volver al foco
      // (ej. otro tab cambió el estado del inventario).
      staleTime: 30_000,
    },
  );

/**
 * Stock del inventario. `mode` decide endpoint:
 *   - "manual"  → /stock           (resumen agrupado)
 *   - "barcode" → /history          (líneas crudas, modo escáner)
 *
 * Devuelve el response del back tal cual; el caller decide si lee `data` o
 * `metadata`.
 */
export const useInventoryStockQuery = (depositId, inventoryId, mode, args) => {
  const fetcher =
    mode === 'barcode'
      ? () => getInventoryStockRawById(depositId, inventoryId, args).then((r) => r.data)
      : () => getInventoryStockById(depositId, inventoryId, args).then((r) => r.data);

  return useQuery(inventoryKeys.stock(depositId, inventoryId, mode, args), fetcher, {
    enabled: Boolean(depositId && inventoryId),
    keepPreviousData: true,
  });
};

/**
 * Mutación para ejecutar acciones de la máquina de estados:
 *   START | REVIEW | CLOSE | CANCEL | SCHEDULE
 *
 * Al completarse con éxito invalida el detalle y el stock del inventario,
 * de modo que la UI se refresca automáticamente sin que el caller tenga
 * que ocuparse.
 *
 * Uso:
 *   const mutation = useInventoryActionMutation(depositId, inventoryId);
 *   mutation.mutate('CLOSE', { onSuccess, onError });
 */
export const useInventoryActionMutation = (depositId, inventoryId) => {
  const queryClient = useQueryClient();

  return useMutation((action) => postInventoryAction(depositId, inventoryId, action), {
    onSuccess: () => {
      queryClient.invalidateQueries(inventoryKeys.detail(depositId, inventoryId));
      // Las cuatro variantes posibles del stock comparten prefijo, basta con
      // invalidar por la cabeza de la clave.
      queryClient.invalidateQueries(['inventory-stock', Number(depositId), Number(inventoryId)]);
    },
  });
};
